/**
 * useProductivitySync — Hook that manages the productivity-module offline sync
 * queue lifecycle.
 *
 * Constructs a single {@link ExtendedSyncQueue} that drains multi-module
 * (tasks/habits/notes/…) changes to the backend. When the app is online and
 * authenticated it flushes anything already queued immediately, re-flushes on
 * reconnect, and stops the queue on cleanup. Must be mounted once in the
 * AppShell so the queue persists across module switches.
 *
 * This mirrors {@link useCalendarSync}, which AppShell already uses for the
 * calendar polling lifecycle.
 *
 * @see Requirements 5.4, 8.1, 12.6, 12.7, 12.8
 */

import { useEffect, useRef } from 'react';
import {
  ExtendedSyncQueue,
  type ExtendedSyncApiClient,
} from '@/services/syncQueue';
import { getIdToken } from '@/services/auth';
import { useAuthStore } from '@/stores/authStore';
import { useSyncStore } from '@/stores/syncStore';

// ─── Constants ───────────────────────────────────────────────────────────────

const API_BASE_URL =
  import.meta.env.VITE_API_URL ?? import.meta.env.VITE_API_BASE_URL ?? '/api';

// ─── API Client ───────────────────────────────────────────────────────────────

/**
 * Create the API client used by the ExtendedSyncQueue to drain multi-module
 * (tasks/habits/notes/…) changes to the backend. Routes each queued item to
 * the module endpoint resolved by the queue, using the deployed API base URL.
 */
function createExtendedSyncApiClient(): ExtendedSyncApiClient {
  return {
    async sendRequest(
      endpoint: string,
      method: string,
      entityId: string,
      payload: Record<string, unknown>
    ): Promise<Record<string, unknown>> {
      const token = await getIdToken();

      // POST (create) targets the collection endpoint; PUT/DELETE target the
      // item path with the entity id appended (e.g. /notes vs /notes/{noteId}).
      const url =
        method === 'POST'
          ? `${API_BASE_URL}${endpoint}`
          : `${API_BASE_URL}${endpoint}/${encodeURIComponent(entityId)}`;

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: method === 'DELETE' ? undefined : JSON.stringify(payload),
      });

      if (!response.ok) {
        // Throw so the queue retries with exponential backoff.
        throw new Error(`Sync request failed: ${response.status}`);
      }

      // Guard against 204 / empty bodies.
      if (response.status === 204) {
        return {};
      }
      const text = await response.text();
      return text ? (JSON.parse(text) as Record<string, unknown>) : {};
    },
  };
}

// ─── Hook ───────────────────────────────────────────────────────────────────

/**
 * Starts the productivity-module offline sync queue when the user is
 * authenticated. Flushes queued changes immediately if online, re-flushes on
 * reconnect, and stops the queue on cleanup / sign-out.
 * Call this once in a top-level component (e.g., AppShell).
 */
export function useProductivitySync(): void {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const queueRef = useRef<ExtendedSyncQueue | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const queue = new ExtendedSyncQueue({
      apiClient: createExtendedSyncApiClient(),
      onQueueSizeChange: (size) => {
        useSyncStore.getState().setQueueSize(size);
      },
      onSyncComplete: () => {
        useSyncStore.getState().setSyncStatus('synced');
      },
    });

    queueRef.current = queue;

    // Expose a force-flush entry point so UI (e.g. the "Sync Now" button in
    // SharedHeader) can trigger an immediate drain.
    useSyncStore.getState().setFlushFn(() => queue.flush());

    function handleOnline() {
      queue.handleReconnect();
    }

    // Drain anything already queued when the app loads online
    // (e.g. the user's pending tasks/habits/notes).
    if (navigator.onLine) {
      void queue.flush();
    }

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
      queue.stop();
      queueRef.current = null;
      useSyncStore.getState().setFlushFn(null);
    };
  }, [isAuthenticated]);
}
